import React from 'react';
import classnames from 'classnames';
import Text from '../../Text/Text';
import Icon from '../../Icon/Icon';
import Fade from '../../Transitions/Fade/Fade';

import './Input.scss';

export interface InputProps extends React.HTMLAttributes<any> {
    /**
     * Ref input
     */
    ref?: any;
    /**
     * Id attr input
     */
    id?: string;
    /**
     * Name attr input
     */
    name: string;
    /**
     * Placeholder attr input
     */
    placeholder?: string;
    /**
     * Type attr input
     */
    type?: 'email' | 'hidden' | 'number' | 'password' | 'search' | 'tel' | 'text' | 'url';
    /**
     * Label attr input
     */
    label?: string;
    /**
     * Error message
     */
    error?: string;
    /**
     * Material style
     * @default false
     */
    material?: boolean;
    /**
     * Read only
     * @default false
     */
    readonly?: boolean;
    /**
     * Disabled
     * @default false
     */
    disabled?: boolean;
    /**
     * Align text
     */
    align?: 'left' | 'right' | 'center';
    /**
     *  Icon left
     */
    iconLeft?: string;
    /**
     *  Icon right
     */
    iconRight?: string;
    /**
     *  Icon color
     */
    iconColor?: any;
    /**
     *  Icon size
     */
    iconSize?: number;
    /**
     * Class name styles
     */
    className?: string;
    /**
     * Input class name styles
     */
    inputClassName?: string;
    /**
     * Style
     */
    style?: any;
    /**
     * Value input
     */
    value?: any;
    /**
     * Function called on click left icon
     */
    onClickLeftIcon?(): any;
    /**
     * Function called on click left icon
     */
    onClickRightIcon?(): any;
    /**
     * Function called on change input
     */
    onChange?(event?: any): any;
    /**
     * Other props
     */
    [otherProps: string]: any;
}

//type ComponentProps = InputProps | React.HTMLAttributes<InputProps>;

const Input: React.FC<InputProps> = React.forwardRef(
    (
        {
            id,
            name,
            label,
            placeholder,
            type = 'text',
            error,
            disabled = false,
            material = false,
            readonly = false,
            align = 'left',
            iconLeft,
            iconRight,
            iconColor = '',
            iconSize = 20,
            className,
            inputClassName,
            style,
            value,
            onClickLeftIcon,
            onClickRightIcon,
            onChange = () => {},
            ...otherProps
        },
        ref
    ) => {
        const classNames = classnames(['input', { disabled }, className]);
        const inputClassNames = classnames([
            'input__input',
            { disabled, readonly, error: !!error, material },
            align,
            inputClassName,
        ]);
        const handleChange = (event: any) => onChange({ ...event, value: event?.target?.value });
        const handleClickLeftIcon = () => onClickLeftIcon && onClickLeftIcon();
        const handleClickRightIcon = () => onClickRightIcon && onClickRightIcon();

        return (
            <div className={classNames} style={style}>
                {label && !material && (
                    <Text tag="label" typo="label" for={id} className="input__label">
                        {label}
                    </Text>
                )}
                <div className="input__wrapper">
                    {iconLeft ? (
                        <Icon
                            name={iconLeft}
                            color={iconColor}
                            size={iconSize}
                            className="input__icon input__icon--left"
                            clickable={!!onClickLeftIcon}
                            onClick={handleClickLeftIcon}
                        />
                    ) : undefined}
                    {iconRight ? (
                        <Icon
                            name={iconRight}
                            color={iconColor}
                            size={iconSize}
                            className="input__icon input__icon--right"
                            clickable={!!onClickRightIcon}
                            onClick={handleClickRightIcon}
                        />
                    ) : undefined}
                    <input
                        ref={ref}
                        id={id}
                        type={type}
                        name={name}
                        value={value}
                        placeholder={material ? label : placeholder}
                        disabled={disabled}
                        className={inputClassNames}
                        onChange={handleChange}
                        {...otherProps}
                    />
                    {label && material && (
                        <Text tag="label" typo="label" weight="medium" for={id} className="input__label--material">
                            {label}
                        </Text>
                    )}
                </div>
                {!!error && (
                    <Fade>
                        <Text tag="span" size="12" color="error" className="input__error">
                            {error}
                        </Text>
                    </Fade>
                )}
            </div>
        );
    }
);

export default Input;
